// Scenen samler alle lag: kort, ledninger, strømprikker, byer, hold, trusler, effekter og lup.
// Kortet ligger under kameraets transform; resten tegnes i skærmkoordinater ud fra visningspositioner.

import { Container, Graphics, Matrix, RenderTexture, Sprite } from 'pixi.js';
import type { Application, Filter } from 'pixi.js';
import type { LevelDef, SimEvent, State } from '../sim';
import { clockFull } from '../sim/cities';
import { CALM_CHZ } from '../sim/threats';
import { pairKey } from '../sim/grid';
import type { Atlas } from './atlas';
import { BIG_ZOOM, Camera, MAX_STRETCH, MAX_ZOOM, PHONE_STRETCH } from './camera';
import type { CamView, Rect } from './camera';
import { CitiesLayer, isLit, type MapObject, type ScreenRect } from './cities-layer';
import { type Blocker, type Box, boxesAt, clearAt, declutter } from './declutter';
import { FlowDots } from './flow-dots';
import { Fx } from './fx';
import { avoidBend, bez, bezDist, bezLen, controlBend, relax } from './geo';
import type { P } from './geo';
import { GridLayer, lineWidth } from './grid-layer';
import { MapLayer } from './map-layer';
import { C } from './palette';
import type { Quality } from './quality';
import { ThreatsLayer } from './threats-layer';
import { Tweens, ease } from './tween';
import { UnitsLayer } from './units-layer';
import type { LineGeo, View } from './view';

export interface SceneOptions {
  mobile: boolean;
  calm: boolean;
  /** Kaldes når en by får strøm (til lyd og skærmlæser). */
  onCityLit: (id: string) => void;
  /** Kameraet glider af sig selv for at vise noder uden for billedet (makkerrum siger det højt). */
  onReframe?: () => void;
}

const LOUPE_R = 62;
const LOUPE_ZOOM = 1.8;
/**
 * Spor: kortet. Har spilleren selv panoreret eller zoomet, tager kameraet først over igen, når en ny
 * node dukker op eller året skifter, og først når fingeren har været væk så længe.
 */
const USER_IDLE_MS = 2500;
/** Kameraets glidning hen til nettet. */
const FRAME_GLIDE_MS = 600;
/** Så længe venter kameraet, efter at fingeren slap, en menu lukkede, eller spilleren byggede. */
const RELEASE_HOLD_MS = 2000;
/** Så længe står kameraet stille efter et byg eller en anden handling: tid til at bygge videre. */
const ACTION_HOLD_MS = 3000;

/** Den første strøm på en ny ledning: hurtig nok til aldrig at stå i vejen. */
const SURGE_MS = 420;

export class Scene {
  readonly cam = new Camera();
  readonly tweens = new Tweens();
  readonly fx: Fx;
  private root = new Container();
  private map: MapLayer;
  private grid = new GridLayer();
  private dots: FlowDots;
  readonly cities: CitiesLayer;
  private units: UnitsLayer;
  readonly threats = new ThreatsLayer();
  private flash = new Graphics();
  /** Snore fra en skubbet figur tilbage til dens ting (declutter). */
  private tethers = new Graphics();
  private flashA = { a: 0 };
  /** Lynglimtets farve: hvid ved storm, gul ved Lynreserve, varm ved hedebølge. */
  private flashC: number = C.white;
  /** Næste puls fra en aktiv power-up (ms). */
  private pulseAt = 0;
  private loupeRT: RenderTexture | null = null;
  private loupeC = new Container();
  private loupeSprite: Sprite | null = null;
  private loupeRing = new Graphics();
  private loupeAt: P | null = null;

  level: LevelDef | null = null;
  private state: State | null = null;
  private real = new Map<string, P>();
  private disp = new Map<string, P>();
  private pos = new Map<string, P>();
  private lines = new Map<string, LineGeo>();
  private drawIn = new Map<string, number>();
  /** Tweens for ledninger, der tegnes ind lige nu. Kopieres til drawIn én gang pr. frame. */
  private drawing = new Map<string, { t: number }>();
  private surges = new Map<string, { u: number }>();
  private lit = new Set<string>();
  private bounds: Rect = { x: 0, y: 0, w: 1, h: 1 };
  private safe: Rect = { x: 0, y: 0, w: 1, h: 1 };
  private sigma = 10;
  /** Buen pr. ledning, valgt uden om andre noder. Regnes om, når kortet lægges om, eller en node dukker op. */
  private bends = new Map<string, number>();
  private bendKey = '';
  private view: View | null = null;
  /** Hit-stop: verden fryser et øjeblik ved udfald og reddede ledninger. */
  private freezeUntil = 0;
  /** Spilleren trækker en ledning: kameraet følger ikke nettet imens. */
  holdFrame = false;
  /**
   * En finger er nede på en node eller trækker: et glid, der allerede er i gang, fryser, til fingeren
   * slipper (IGN-løkke 2, runde 2: "kameraet reframer midt i et træk, så fingeren rammer forkert").
   */
  fingerDown = false;
  /** Spillet holder pause: kameraet står stille, så man kan kigge i ro. main.ts sætter det. */
  holdPaused = false;
  /** Fingeren slap lige: kameraet venter lidt endnu, så målet ikke flytter sig under næste tryk. */
  private releasedAt = -Infinity;
  private wasHeld = false;
  /**
   * Kameraets billeder de seneste 400 ms. Spor: telefonkort: en finger, der lander, hvor byen stod
   * for et øjeblik siden (kameraet gled imens), er stadig et træk fra byen, ikke en panorering.
   */
  private recent: Array<{ t: number; v: CamView }> = [];
  /** Den by med løbende ur, der står uden for billedet lige nu, og hvilken vej den ligger (main.ts viser en chip). */
  offscreen: { id: string; dx: number; dy: number } | null = null;
  private glow: Filter | null = null;
  private glowLoading = false;
  selected: string | null = null;
  /** Noder, hintet nævner (main.ts sætter dem). */
  named: ReadonlySet<string> = new Set();
  targets: ReadonlySet<string> | null = null;
  /** DOM-flader over kortet (tankebobler), som navneskilte går uden om. main.ts sætter dem. */
  overlays: ScreenRect[] = [];
  /** De af dem, der står længe (hintet). Et navn viger for dem med det samme. main.ts sætter dem. */
  firm: ScreenRect[] = [];

  constructor(
    private app: Application,
    readonly atlas: Atlas,
    private quality: Quality,
    private opts: SceneOptions,
  ) {
    this.fx = new Fx(atlas);
    this.fx.shakeEnabled = !opts.mobile && !opts.calm;
    this.fx.budget = quality.settings.particles;
    this.map = new MapLayer(atlas);
    this.dots = new FlowDots(atlas.tex('dot'));
    this.cities = new CitiesLayer(atlas, this.tweens, this.fx);
    this.units = new UnitsLayer((w) => this.fx.sparks(w, 2, C.yellow));
    this.root.isRenderGroup = true;
    this.root.addChild(
      this.map.world,
      this.grid.pylons,
      this.grid.g,
      this.dots.pc,
      this.threats.band,
      this.cities.c,
      this.tethers,
      this.threats.c,
      // Montørbilerne kører over stormskyen: man skal kunne se, at hjælpen er på vej.
      this.units.c,
      this.fx.c,
      this.cities.labels,
      this.grid.planned,
      this.grid.preview,
    );
    app.stage.addChild(this.root, this.flash, this.loupeC);
    this.loupeC.visible = false;
    this.loupeC.addChild(this.loupeRing);
  }

  get calm(): boolean {
    return this.opts.calm;
  }

  /** Indstillingerne: rolig bevægelse (ingen hop, hit-stop og bobling) og ryst for sig. */
  setMotion(calm: boolean, shake: boolean): void {
    this.opts.calm = calm;
    this.fx.shakeEnabled = shake && !calm;
  }

  /** Må verden fryses? Falsk i makkerrum: der ejer rummet uret, og et frys ville give hak. */
  freezeOk = true;

  /** Frys verden i `ms` millisekunder (hit-stop). Slået fra ved rolig bevægelse. */
  hitStop(ms: number): void {
    if (this.opts.calm || !this.freezeOk) return;
    this.freezeUntil = Math.max(this.freezeUntil, performance.now() + ms);
  }

  /** Tidsfaktor for denne frame: næsten stilstand under hit-stop, ellers 1. */
  timeScale(now: number): number {
    return now < this.freezeUntil ? 0.04 : 1;
  }

  /** Glød på effektlaget, kun på højt trin. Filteret hentes først, når der er brug for det. */
  private applyGlow(): void {
    const want = this.quality.settings.glow;
    if (!want) {
      this.fx.c.filters = [];
      return;
    }
    if (this.glow) {
      this.fx.c.filters = [this.glow];
      return;
    }
    if (this.glowLoading) return;
    this.glowLoading = true;
    import('pixi-filters/glow')
      .then(({ GlowFilter }) => {
        this.glow = new GlowFilter({ distance: 8, outerStrength: 1.4, innerStrength: 0, color: C.glowWarm, alpha: 0.7, quality: 0.15 });
        if (this.quality.settings.glow) this.fx.c.filters = [this.glow];
      })
      .catch(() => undefined);
  }
  private onLand = (p: P): boolean => this.map.onLand(p);

  /** Ny bane: visningspositioner, kamera og kort. */
  setLevel(state: State, level: LevelDef): void {
    this.state = state;
    this.level = level;
    this.real.clear();
    for (const n of state.nodes) this.real.set(n.id, { x: n.x, y: n.y });
    this.cities.reset();
    this.units.reset();
    this.threats.reset();
    this.fx.clear();
    this.tweens.clear();
    this.drawIn.clear();
    this.drawing.clear();
    this.surges.clear();
    this.lit = new Set(state.nodes.filter((n) => n.active && isLit(n)).map((n) => n.id));
    this.selected = null;
    this.targets = null;
    this.cam.moved = false;
    this.reframe = false;
    this.activeN = -1;
    this.relayout();
  }

  /** Det frie felt mellem HUD og værktøjslinje (px). */
  layout(safe: Rect): void {
    // IGN-løkke runde 4: HUD'en skifter bredde, når kassen får et ciffer mere, og ResizeObserver
    // kaldte så layout igen med det samme felt. Hver gang blev nettet lagt ud på ny, og værtens kort
    // hoppede 35-40 px efter næsten hver handling. Et uændret felt rører nu ikke kameraet.
    const o = this.safe;
    const same = Math.abs(o.x - safe.x) < 1 && Math.abs(o.y - safe.y) < 1 && Math.abs(o.w - safe.w) < 1 && Math.abs(o.h - safe.h) < 1;
    this.safe = safe;
    if (same && this.laidOut) return;
    if (this.state) this.relayout();
  }
  /** Er banen lagt ud i det nuværende felt? */
  private laidOut = false;

  private relayout(): void {
    const state = this.state;
    if (!state) return;
    this.laidOut = true;
    const pts = [...this.real.values()];
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const p of pts) {
      x0 = Math.min(x0, p.x);
      y0 = Math.min(y0, p.y);
      x1 = Math.max(x1, p.x);
      y1 = Math.max(y1, p.y);
    }
    // Plads til husenes højde over foden og navne under.
    const s = this.safe;
    // Luften skalerer med feltet, så en telefon på tværs ikke bruger en tredjedel af højden på luft.
    // Spor: telefonkort: på tværs 15 % (før 12 %): ved 844x390 var 42 px lavere end et hus, så
    // Thorsmindes tag stod over feltet, og navn og stormsky trængtes i vejrstrimlens række.
    const top = Math.round(Math.max(40, Math.min(70, s.h * (this.wide ? 0.15 : 0.16))));
    const bottom = Math.round(Math.max(18, Math.min(30, s.h * 0.08)));
    const side = Math.round(Math.max(20, Math.min(34, s.w * 0.05)));
    const inner: Rect = { x: s.x + side, y: s.y + top, w: Math.max(80, s.w - side * 2), h: Math.max(80, s.h - top - bottom) };
    const pad = 4;
    const raw: Rect = { x: x0 - pad, y: y0 - pad, w: x1 - x0 + pad * 2, h: y1 - y0 + pad * 2 };
    // Højformat: et bredt net strækkes lidt på højden, så det fylder skærmen i stedet for en stribe.
    const stretch = this.maxStretch;
    // Telefon på siden: et højt net klemmes lidt i højden, så det bliver bredere og noderne får
    // fingerafstand (IGN-løkke 2, runde 2: 30-45 px mellem Horsens, Kolding og Vejle ved 844x390).
    const squash = this.wide ? 1 / MAX_STRETCH : 1;
    this.cam.fit(raw, inner, stretch, squash);
    const nodes = state.nodes.map((n) => ({ id: n.id, x: n.x, y: n.y }));
    // To runder: skub noderne fra hinanden, tilpas kameraet, og skub igen med den nye skala.
    // Afstanden vokser med pladsen: en stor skærm får luft mellem byerne, en telefon får nok til fingre.
    const roomy = Math.sqrt((inner.w * inner.h) / Math.max(1, nodes.length)) * 0.5;
    // På en telefon er gulvet en fingerspids, men aldrig mere end feltet kan bære: på tværs (844x390)
    // skubbede 84 px byerne forbi hinanden, så kortet ikke længere lignede Danmark (IGN runde 1).
    // Klemt i højden (telefon på siden) er der bredde til lidt mere: 64 px, så husene ikke rører hinanden.
    const floor = this.opts.mobile ? Math.max(this.wide ? 64 : 56, Math.min(84, roomy * 1.4)) : 92;
    const sepPx = Math.max(floor, Math.min(150, roomy));
    for (let round = 0; round < 3; round++) {
      const minSep = sepPx / this.cam.fitScale;
      this.sigma = minSep * 1.4;
      // Højformat er smalt og strækkes på højden: en klynge på syv (Holstebro-egnen) skal kunne folde
      // sig helt ud, ellers står byerne i en stak. Loftet på flytningen gælder kun de andre formater.
      // Klemt i højden skubbes noderne fra hinanden i skærmens mål, ikke kortets: ellers står de for
      // tæt på lodret, når billedet klemmes.
      const ky = Math.min(1, this.cam.ky);
      const placed = relax(ky < 1 ? nodes.map((n) => ({ id: n.id, x: n.x, y: n.y * ky })) : nodes, minSep, 120, this.portrait ? Infinity : minSep * 0.8);
      this.disp = ky < 1 ? new Map([...placed].map(([id, p]) => [id, { x: p.x, y: p.y / ky }])) : placed;
      x0 = y0 = Infinity;
      x1 = y1 = -Infinity;
      for (const p of this.disp.values()) {
        x0 = Math.min(x0, p.x);
        y0 = Math.min(y0, p.y);
        x1 = Math.max(x1, p.x);
        y1 = Math.max(y1, p.y);
      }
      this.bounds = { x: x0 - pad, y: y0 - pad, w: x1 - x0 + pad * 2, h: y1 - y0 + pad * 2 };
      this.cam.fit(this.bounds, inner, stretch, squash);
    }
    this.inner = inner;
    this.cam.zoomCap = !this.opts.mobile && inner.w >= 1400 && inner.h >= 700 ? BIG_ZOOM : MAX_ZOOM;
    this.buildMap();
    // Startbilledet: i højformat zoomet ind på de aktive noder (se autoFrame).
    this.frameKey = '';
    this.seen = null;
    this.autoFrame(true);
  }

  /**
   * Højst så meget lodret stræk. Spor: telefonkort: en telefon på højkant må strække mere (1,8), for
   * et bredt net (bane 4: Viborg til Aarhus) stod i en stribe midt på skærmen med en tredjedel tom
   * himmel over og under, mens Ulstrup, Hinnerup, Trige og Studstrupværket trængtes om navnepladserne.
   */
  private get maxStretch(): number {
    if (!this.portrait) return 1;
    return this.opts.mobile ? PHONE_STRETCH : MAX_STRETCH;
  }

  private get portrait(): boolean {
    return this.safe.h > this.safe.w * 1.3;
  }

  /** Lavt og bredt felt på en telefon (på siden): her må kortet klemmes lidt i højden. */
  private get wide(): boolean {
    return !!this.opts.mobile && this.safe.w > this.safe.h * 1.3;
  }

  /** Feltet inden for det frie felt, hvor noderne står (luft til husenes højde og navne). */
  private inner: Rect = { x: 0, y: 0, w: 1, h: 1 };
  /** Det frie felt mellem HUD og værktøjer (lærredets koordinater), til bykortet. */
  get mapArea(): Rect {
    return this.inner;
  }
  private frameKey = '';
  /** Antal aktive noder sidst: vokser det, må kameraet tage over igen (blødt, når spilleren er færdig). */
  private activeN = -1;
  /** En ny node eller et nyt år venter på, at spilleren slipper kameraet, før det rammer nettet ind. */
  private reframe = false;
  /**
   * Alle noder, der har været aktive på banen (visnings-km). Kameraet zoomer kun ud: før pumpede det
   * ud og ind ved hver ny by, så målene flyttede sig under fingeren (playtest runde 2).
   */
  private seen: Rect | null = null;
  /**
   * Spilleren har gjort noget (bygget, svaret, valgt): kameraet står stille lidt endnu, så det, man
   * lige byggede, ikke glider væk under fingeren (Spor: kortet: kameraets delta er 0 efter et byg).
   */
  holdForAction(): void {
    this.quietUntil = performance.now() + ACTION_HOLD_MS;
  }
  /** Kameraet står stille til dette tidspunkt (efter en handling). */
  private quietUntil = -Infinity;

  /** Nyt år: kameraet må ramme nettet ind igen, også hvis spilleren selv har flyttet det. */
  newYear(calm = false): void {
    // Makkerrum (spor "coop"): ingen uanmeldt zoom ved årsskiftet. Kameraet flytter sig kun, hvis nye
    // byer ligger uden for billedet, og så siger det det (onReframe).
    if (calm) return;
    this.reframe = true;
    this.yearFrame = true;
  }
  /**
   * Årsskiftet er kameraets rolige øjeblik (gaveskærmen står foran): så fylder og centrerer det nettet
   * helt, også når alle noder allerede kan ses. Resten af året flytter en synlig ny by intet.
   */
  private yearFrame = false;

  /** Kassen om de aktive noder (visnings-km). */
  private activeBox(): Rect | null {
    const state = this.state;
    if (!state) return null;
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    // IGN-løkke runde 4 tog alle aktive noder med, fordi træneren pegede på et værk uden for skærmen.
    // IGN-løkke 2, runde 2: så stod bane 1's første net klemt i et hjørne ved 1280x800, fordi det fjerne
    // Nordjyllandsværket trak kassen. Nu tæller et værk uden ledninger kun med, når hintet nævner det
    // (`named`), og så bliver det i `seen` resten af året.
    const linked = new Set<string>();
    for (const l of state.lines) {
      linked.add(l.a);
      linked.add(l.b);
    }
    for (const n of state.nodes) {
      if (!n.active || n.phantom) continue;
      if (n.kind === 'plant' && !linked.has(n.id) && !this.named.has(n.id)) continue;
      const p = this.disp.get(n.id);
      if (!p) continue;
      x0 = Math.min(x0, p.x);
      y0 = Math.min(y0, p.y);
      x1 = Math.max(x1, p.x);
      y1 = Math.max(y1, p.y);
    }
    if (x1 < x0) return null;
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  /**
   * Ligger en aktiv by med løbende mørkeur, eller en ny by i sin nådetid, uden for det frie felt lige
   * nu? Playtest runde 3: Thorsminde dukkede op under HUD'en, og uret var halvt gået, før man så den.
   */
  private clockOffscreen(): { id: string; dx: number; dy: number } | null {
    const state = this.state;
    if (!state) return null;
    const s = this.inner;
    for (const n of state.nodes) {
      if (!n.active || !n.city || (n.city.clock <= 0 && n.city.graceTicks <= 0)) continue;
      const p = this.disp.get(n.id);
      if (!p) continue;
      const q = this.cam.toScreen(p);
      if (q.x < s.x || q.y < s.y || q.x > s.x + s.w || q.y > s.y + s.h) {
        const cx = s.x + s.w / 2;
        const cy = s.y + s.h / 2;
        const len = Math.max(1, Math.hypot(q.x - cx, q.y - cy));
        return { id: n.id, dx: (q.x - cx) / len, dy: (q.y - cy) / len };
      }
    }
    return null;
  }

  /** Chippen "Find byen med uret": glid hen, så nettet og byen står i billedet. */
  findClock(): void {
    this.cam.moved = false;
    this.frameKey = '';
    this.reframe = true;
    this.autoFrame(false, true);
  }

  /**
   * Kameraet følger nettet af sig selv, i alle formater: de aktive noder fylder feltet (plus 30 %
   * luft), og det glider på 0,6 s. Spor: kortet: det flytter sig aldrig, mens en finger er nede, mens
   * en menu, et kort eller et bykort står, lige efter et byg eller i pause. Har spilleren selv flyttet
   * kameraet, tager det kun over igen ved en ny node eller et nyt år, og først når spilleren har
   * sluppet det i 2,5 s. En by med løbende ur uden for billedet går foran alt andet end fingeren.
   */
  private autoFrame(instant = false, force = false): void {
    if (this.wasHeld && !this.holdFrame) this.releasedAt = performance.now();
    this.wasHeld = this.holdFrame;
    if (this.holdFrame && !instant) return;
    const state = this.state;
    if (state) {
      let n = 0;
      for (const x of state.nodes) if (x.active && !x.phantom) n++;
      if (this.activeN >= 0 && n > this.activeN) this.reframe = true;
      this.activeN = n;
    }
    const t = performance.now();
    // Spor: telefonkort: en by med ur uden for billedet går ikke længere foran spillerens egne hænder.
    // Lige efter et tryk, et træk, en panorering eller et byg står kameraet stille, og en chip viser
    // vej i stedet (finalen på bane 1: et glid lige før trækket gjorde det til en panorering).
    const hands = t - this.releasedAt < RELEASE_HOLD_MS || t < this.quietUntil || t - this.cam.movedAt < USER_IDLE_MS;
    const settling = this.holdPaused || hands;
    this.offscreen = this.clockOffscreen();
    const urgent = !!this.offscreen && !hands;
    if (!instant && !force && settling && !urgent) return;
    if (this.cam.moved && (urgent || (this.reframe && t - this.cam.movedAt > USER_IDLE_MS))) {
      this.cam.moved = false;
      this.frameKey = '';
    }
    const now = this.activeBox();
    if (!now) return;
    const sb = this.seen;
    const box: Rect = sb
      ? (() => {
          const x0 = Math.min(sb.x, now.x);
          const y0 = Math.min(sb.y, now.y);
          return { x: x0, y: y0, w: Math.max(sb.x + sb.w, now.x + now.w) - x0, h: Math.max(sb.y + sb.h, now.y + now.h) - y0 };
        })()
      : now;
    this.seen = box;
    if (this.cam.moved) return;
    this.reframe = false;
    const year = this.yearFrame;
    this.yearFrame = false;
    const q = (n: number): number => Math.round(n / 8);
    const key = `${q(box.x * 10)},${q(box.y * 10)},${q(box.w * 10)},${q(box.h * 10)}`;
    if (key === this.frameKey && !force && !year) return;
    this.frameKey = key;
    // Står alle aktive noder allerede frit i billedet, rører kameraet sig ikke: en ny by inden for
    // billedet må ikke zoome kortet om (IGN runde 1: "etiketterne bytter plads, når en by dukker op").
    if (!instant && !force && !year && this.allVisible(now)) return;
    const v = this.restView(box);
    if (instant) this.cam.set(v);
    else {
      this.cam.glideTo(v, FRAME_GLIDE_MS);
      if (!force && !year) this.opts.onReframe?.();
    }
  }

  /**
   * Står hele kassen (visnings-km) inden for det frie felt? Spor: kortet: målt mod hele det frie
   * felt, ikke det indre med luft til husene; en ny by, man kan se, flytter ikke kameraet.
   * Spor: telefonkort: målt i det billede, kameraet ender i, ikke midt i et glid. En by, der blev
   * aktiv, mens kameraet gled, stod i mellembilledet og blev regnet for set; glidet fortsatte til
   * det gamle mål, og Solpark Trige på bane 3 (844x390) endte over skærmens kant og blev der.
   */
  private allVisible(box: Rect): boolean {
    const s = this.safe;
    const m = 8;
    const v = this.cam.destination;
    const at = (p: P): P => ({ x: p.x * v.scale + v.ox, y: p.y * v.scale * v.ky + v.oy });
    const a = at({ x: box.x, y: box.y });
    const b = at({ x: box.x + box.w, y: box.y + box.h });
    return a.x >= s.x + m && a.y >= s.y + m && b.x <= s.x + s.w - m && b.y <= s.y + s.h - m;
  }

  /**
   * De aktive noder med 12 % luft (6 % i hver side; feltet har selv plads til huse og navne), mellem
   * hele banen og 1,6 gange den. Højformat må strække lidt på højden; bredformat aldrig. Spor: kortet:
   * 30 % luft lod nettet fylde 65 % af feltet på en desktop; aldrig mindre end hele banen, så byerne
   * altid har mindst deres fingerafstand (relax), og spilleren panorerer hellere end at klemme.
   */
  private restView(box: Rect): CamView {
    // Luften følger hver akse for sig: før gav et bredt net også 8 % af bredden som luft i højden.
    const mx = box.w * 0.06;
    // Telefon på siden: højden er det knappe mål, og feltet har allerede luft til huse og navne.
    const my = this.wide ? 0 : box.h * 0.06;
    const r: Rect = { x: box.x - mx, y: box.y - my, w: box.w + mx * 2, h: box.h + my * 2 };
    // IGN-løkke 2, runde 2: ved 1920x1080 fyldte fire noder en fjerdedel af skærmen med 13 px navne.
    // En stor skærm må zoome helt ind (som spilleren selv kan); telefonen holder 1,6, så kameraet ikke
    // skal zoome ud ved hver ny by.
    const maxZoom = this.opts.mobile ? 1.6 : this.cam.zoomCap;
    return this.cam.frame(r, this.inner, { maxZoom, maxStretch: this.maxStretch, minStretch: this.wide ? 1 / MAX_STRETCH : 1, margin: this.wide ? 4 : 12 });
  }

  private buildMap(): void {
    if (!this.state) return;
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    // Altid til udgangsbilledet (hele banen), også når kameraet står zoomet ind.
    const a = this.cam.homeToWorld({ x: -40, y: -40 });
    const b = this.cam.homeToWorld({ x: w + 40, y: h + 40 });
    const area: Rect = { x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
    this.map.build(area, this.cam.fitScale, [...this.disp.values()], this.state.seed, this.quality.settings.decor);
  }

  /** Kvaliteten skiftede: opløsning og pynt. */
  qualityChanged(): void {
    const q = this.quality.settings;
    const r = this.app.renderer;
    r.resize(this.app.screen.width, this.app.screen.height, q.resolution);
    this.fx.budget = q.particles;
    this.applyGlow();
    this.buildMap();
    // Tegn med det samme, så skiftet ikke giver en sort frame.
    if (this.state) this.frame(this.state, performance.now(), 0, 1);
    this.render();
  }

  /** Vis hele banen igen (dobbelttryk på tom grund eller knappen). */
  recenter(): void {
    // Tilbage til nettet, og kameraet følger det af sig selv igen.
    this.cam.moved = false;
    this.frameKey = '';
    this.autoFrame(false, true);
  }

  /** Står kortet i udgangsbilledet? Ellers vises knappen, der centrerer det. */
  get atHome(): boolean {
    // Følger kameraet selv nettet (højformat) eller kortet, er der ikke noget at centrere.
    if (!this.cam.moved) return true;
    return this.cam.atHome;
  }

  /** Ægte km til visnings-km: noderne er skubbet lidt, og alt i nærheden følger med. */
  warp = (p: P): P => {
    let wx = 0;
    let wy = 0;
    let ws = 0;
    const s2 = 2 * this.sigma * this.sigma;
    for (const [id, r] of this.real) {
      const d = this.disp.get(id);
      if (!d) continue;
      const dd = (p.x - r.x) ** 2 + (p.y - r.y) ** 2;
      const w = Math.exp(-dd / s2);
      if (w < 1e-4) continue;
      wx += w * (d.x - r.x);
      wy += w * (d.y - r.y);
      ws += w;
    }
    const n = Math.max(1, ws);
    return { x: p.x + wx / n, y: p.y + wy / n };
  };

  /** Buen for en ledning mellem a og b, så den går uden om de andre noders ikoner. */
  private bendFor(state: State, aId: string, bId: string): number {
    const a = this.disp.get(aId);
    const b = this.disp.get(bId);
    if (!a || !b) return 0;
    const obstacles: P[] = [];
    for (const n of state.nodes) {
      if (!n.active || n.id === aId || n.id === bId) continue;
      const p = this.disp.get(n.id);
      if (p) obstacles.push(p);
    }
    // Ikonet fylder omkring en tredjedel af mindsteafstanden mellem noder.
    return avoidBend(a, b, pairKey(aId, bId), obstacles, (this.sigma / 1.4) * 0.38);
  }

  /** Lige før hvert sim-tick. */
  snapshot(state: State): void {
    this.units.snapshot(state);
  }

  frame(state: State, now: number, dt: number, alpha: number): void {
    this.state = state;
    this.autoFrame();
    this.cam.update(this.fingerDown ? 0 : dt);
    {
      const v = this.cam.view();
      const last = this.recent[this.recent.length - 1];
      // `t` er sidste gang billedet stod på skærmen: et billede, der stod stille i ti sekunder, er
      // stadig det, fingeren sigter efter, lige når et glid begynder.
      if (last && last.v.ox === v.ox && last.v.oy === v.oy && last.v.scale === v.scale && last.v.ky === v.ky) last.t = now;
      else this.recent.push({ t: now, v });
      while (this.recent.length > 1 && now - (this.recent[0]?.t ?? now) > 400) this.recent.shift();
    }
    this.tweens.update(dt);
    for (const [id, o] of this.drawing) this.drawIn.set(id, o.t);
    const k = this.cam.k * (this.opts.mobile ? 0.88 : 1);
    this.pos.clear();
    for (const [id, d] of this.disp) this.pos.set(id, this.cam.toScreen(d));
    this.lines.clear();
    let active = 0;
    for (const n of state.nodes) if (n.active) active++;
    const key = `${active}:${this.sigma}`;
    if (key !== this.bendKey) {
      this.bendKey = key;
      this.bends.clear();
    }
    for (const l of state.lines) {
      const a = this.pos.get(l.a);
      const b = this.pos.get(l.b);
      if (!a || !b) continue;
      let bend = this.bends.get(l.id);
      if (bend === undefined) {
        bend = this.bendFor(state, l.a, l.b);
        this.bends.set(l.id, bend);
      }
      const c = controlBend(a, b, bend);
      this.lines.set(l.id, { id: l.id, a, c, b, len: bezLen(a, c, b) });
    }
    const atRisk = new Set<string>();
    for (const t of state.threats) if (t.phase === 'warning' && !t.shooed) for (const id of t.atRisk) atRisk.add(id);
    const playing = state.phase === 'playing';
    const v: View = {
      state,
      cam: this.cam,
      atlas: this.atlas,
      tweens: this.tweens,
      quality: this.quality.settings,
      k,
      now,
      dt,
      alpha: playing ? alpha : 1,
      pos: this.pos,
      disp: this.disp,
      lines: this.lines,
      warp: this.warp,
      selected: this.selected,
      named: this.named,
      targets: this.targets,
      atRisk,
      drawIn: this.drawIn,
      surges: this.surges,
      calm: this.opts.calm,
      width: this.app.screen.width,
      clockFull: clockFull(state),
      clock: state.clockTick + (playing ? alpha : 0),
      bounds: this.bounds,
      mobile: this.opts.mobile,
      onLand: this.onLand,
    };
    this.view = v;
    this.map.update(v);
    this.grid.update(v);
    this.dots.update(v);
    this.threats.update(v);
    // Navneskilte går uden om trusselstal og svævende tal.
    // Byernes egne små ting (skilte, hjerter) går navnene selv uden om (cities-layer: placeLabels).
    this.cities.obstacles = [...this.threats.rects(), ...this.fx.textRects(v), ...this.overlays];
    this.cities.firm = this.firm;
    // Spor: telefonkort: navnene holder sig inden for det frie felt (før 36 px op i vejrstrimlens
    // række og 24 px ned mod værktøjslinjen, hvor maptest så dem som klippede). 4 px begge veje.
    this.cities.band = { x0: this.safe.x, y0: Math.max(0, this.safe.y - 4), x1: this.safe.x + this.safe.w, y1: this.safe.y + this.safe.h + 4 };
    this.cities.green = state.flow.servedMw > 0 ? state.flow.greenMw / state.flow.servedMw : 0;
    this.cities.update(v);
    this.units.update(v);
    this.declutter(state, now);
    const shake = this.fx.update(v);
    this.root.position.set(shake.x, shake.y);
    this.checkLit(state);
    // Lynglimt: kort og svagt, så det aldrig skjuler noget.
    this.flash.clear();
    if (this.flashA.a > 0.001) {
      this.flash.rect(0, 0, this.app.screen.width, this.app.screen.height).fill({ color: this.flashC, alpha: this.flashA.a });
    }
    this.powerPulse(state, now);
    this.renderLoupe();
  }

  /**
   * Spor: telefonkort: hændelsesfigurer og holdende montørhold står ved deres ting, men aldrig oven i
   * en anden nodes hus, et navn, en nodes måler eller en anden figur (declutter.ts). Forskydningen
   * glider på plads (80 ms), og skyer tones ned over navne.
   */
  private declutterAt = 0;
  /** Testkrog: figurer, der ikke fandt en fri plads i sidste billede. */
  private stuck: string[] = [];
  /** Testkrog: en figur glider stadig mod sin nye plads (maptest måler det færdige billede). */
  private gliding = false;

  private declutter(state: State, now: number): void {
    // Væguret, ikke spillets tid: et åbent hændelseskort pauser spillet, men figuren skal stadig
    // træde ud af navnet under det.
    const dt = Math.min(100, Math.max(0, now - this.declutterAt));
    this.declutterAt = now;
    const nodes = this.cities.nodeBoxes();
    const blockers: Blocker[] = [];
    const labels: Box[] = [];
    for (const n of nodes) {
      // Huset i hvile: et glad hop må ikke skubbe figurerne frem og tilbage.
      const b = n.rest;
      blockers.push({ owner: n.id, kind: 'house', box: { x: b.x + b.w * 0.18, y: b.y + b.h * 0.18, w: b.w * 0.64, h: b.h * 0.64 } });
      if (n.label) {
        blockers.push({ owner: n.id, kind: 'label', box: n.label });
        labels.push(n.label);
      }
    }
    for (const o of this.cities.objectBoxes()) blockers.push({ owner: o.owner, kind: 'object', box: o.box });
    this.threats.fadeOver(labels, state);
    const items = [...this.threats.pieces(state), ...this.units.pieces()];
    if (!items.length) {
      this.tethers.clear();
      this.gliding = false;
      return;
    }
    const k = this.k;
    const pad = 40;
    const bounds = { x: -pad, y: -pad, w: this.app.screen.width + pad * 2, h: this.app.screen.height + pad * 2 };
    const res = declutter(
      items.map((i) => i.piece),
      blockers,
      Math.max(8, 9 * k),
      // 20 ringe (op til ~160 px): i en tæt klynge på telefonen (Struer, Skive, Vinderup; Viborg, Tjele)
      // var seks for få, og en hændelse, der fløj ind over Horsens og Skanderborg på bane 3, lå lige uden
      // for 16. En figur langt fra sin ting får en tynd snor tilbage til den.
      20,
      bounds,
      (this.stuck = []),
    );
    // En figur, der lige er dukket op, står straks på sin frie plads; en, der må flytte sig, glider,
    // men kun så længe vejen er fri. Ellers træder den direkte på plads: en hændelse, der fløj ind
    // over Aalborg på bane 5, gled ellers hen over husets tag på vej mod den nye plads.
    const f = 1 - Math.exp(-Math.max(0, dt) / 45);
    const goal = items.map((it, i) => boxesAt(it.piece, res[i] ?? { x: 0, y: 0 }));
    const t = this.tethers.clear();
    this.gliding = false;
    items.forEach((it, i) => {
      const o = res[i] ?? { x: 0, y: 0 };
      const n = it.view.nudge;
      it.view.target = o;
      let nu = o;
      if (it.view.placed && Math.hypot(o.x - n.x, o.y - n.y) >= 0.5) {
        const mid = { x: n.x + (o.x - n.x) * f, y: n.y + (o.y - n.y) * f };
        const others = goal.filter((_, j) => j !== i).flat();
        if (clearAt(it.piece, mid, blockers, others)) {
          nu = mid;
          this.gliding = true;
        }
      }
      it.view.placed = true;
      it.view.nudge = nu;
      // Laget har allerede tegnet figuren med den gamle forskydning i dette billede: flyt den nu, så
      // billedet viser det, der lige blev regnet ud (ellers stod en ny figur ét billede på et navn).
      const dx = nu.x - n.x;
      const dy = nu.y - n.y;
      if (dx || dy) for (const c of it.parts) if (c.visible) c.position.set(c.x + dx, c.y + dy);
      // Snoren: fra tingen (figurens hvilested) til figuren, når den står mere end en håndsbredde væk.
      const b = it.piece.boxes[0];
      if (!b || Math.hypot(nu.x, nu.y) < 22 * k) return;
      const fx = b.x + b.w / 2 - n.x + nu.x;
      const fy = b.y + b.h / 2 - n.y + nu.y;
      const ax = fx - nu.x;
      const ay = fy - nu.y;
      t.moveTo(ax, ay).lineTo(fx, fy).stroke({ width: Math.max(1.2, 1.6 * k), color: C.ink, alpha: 0.35, cap: 'round' });
      t.circle(ax, ay, Math.max(2, 2.6 * k)).fill({ color: C.ink, alpha: 0.45 });
    });
  }

  private checkLit(state: State): void {
    for (const n of state.nodes) {
      if (!n.city || !n.active) continue;
      const on = isLit(n);
      const had = this.lit.has(n.id);
      if (on && !had) {
        this.lit.add(n.id);
        const d = this.disp.get(n.id);
        if (d) {
          this.fx.ring(d, C.yellow, 1.2);
          this.fx.sparks({ x: d.x, y: d.y }, 6, C.yellow);
        }
        this.cities.squash(n.id);
        this.lightWave(n.id);
        this.opts.onCityLit(n.id);
      } else if (!on && had) {
        this.lit.delete(n.id);
      }
    }
  }

  /** Et kort glimt over hele skærmen. Rolig bevægelse får det ikke. */
  private glimt(color: number, a: number, ms: number): void {
    if (this.opts.calm) return;
    this.flashC = color;
    this.flashA.a = a;
    this.tweens.to(this.flashA, { a: 0 }, ms, ease.outQuad);
  }

  /** Mens en power-up kører, pulser den stille, så man kan se, hvor hjælpen er. */
  private powerPulse(state: State, now: number): void {
    if (now < this.pulseAt || state.phase !== 'playing') return;
    this.pulseAt = now + 900;
    const t = state.clockTick;
    const pw = state.power;
    if (t < pw.norway.until) {
      const d = this.disp.get(pw.norway.target);
      if (d) this.fx.ring(d, C.turquoise, 0.7);
    }
    if (t < pw.shed.until) {
      const d = this.disp.get(pw.shed.target);
      if (d) this.fx.ring(d, C.petrol, 0.55);
    }
    if (t < pw.fcr.until && !this.opts.calm) {
      // Lynreserven: små gule gnister fra de byer, der har strøm.
      const lit = this.cities.litIds();
      const id = lit[Math.floor(Math.random() * lit.length)];
      const d = id ? this.disp.get(id) : undefined;
      if (d) this.fx.sparks({ x: d.x, y: d.y - 12 }, 4, C.yellow);
    }
  }

  /** Lysbølge: når en by tænder, hopper de andre byer med strøm én efter én, nærmeste først. */
  private lightWave(from: string): void {
    if (this.opts.calm) return;
    const a = this.pos.get(from);
    if (!a) return;
    this.cities.hop(from, 0, 12);
    for (const id of this.cities.litIds()) {
      if (id === from) continue;
      const b = this.pos.get(id);
      const d = this.disp.get(id);
      if (!b || !d) continue;
      const delay = Math.min(900, Math.hypot(b.x - a.x, b.y - a.y) * 1.6);
      this.cities.hop(id, delay, 7);
      // Et enkelt, svagt ekko: de tændte byer skal nikke med, ikke råbe om opmærksomhed.
      this.fx.ring(d, C.yellow, 0.45, delay, 0.35, 1);
    }
  }

  /** Sejr: lysbølgen går fra vest mod øst, husene hopper, og fyrværkeri stiger over kortet. */
  victory(): void {
    const st = this.state;
    if (!st) return;
    const cities = st.nodes.filter((n) => n.active && n.city && this.disp.has(n.id));
    cities.sort((a, b) => (this.disp.get(a.id)?.x ?? 0) - (this.disp.get(b.id)?.x ?? 0));
    const x0 = this.disp.get(cities[0]?.id ?? '')?.x ?? 0;
    const x1 = this.disp.get(cities[cities.length - 1]?.id ?? '')?.x ?? 1;
    const span = Math.max(1, x1 - x0);
    cities.forEach((n) => {
      const d = this.disp.get(n.id);
      if (!d) return;
      const delay = ((d.x - x0) / span) * 1100;
      this.fx.ring(d, C.yellow, 1.3, delay);
      if (!this.opts.calm) {
        this.cities.hop(n.id, delay, 14);
        this.cities.hop(n.id, delay + 700, 8);
      }
    });
    // Fyrværkeri i verden (aldrig i UI'et). Rolig bevægelse får et par stille raketter.
    const n = this.opts.calm ? 3 : Math.min(18, Math.round(this.quality.settings.particles / 30));
    for (let i = 0; i < n; i++) {
      const c = cities[i % Math.max(1, cities.length)];
      const d = c ? this.disp.get(c.id) : null;
      if (!d) continue;
      this.fx.firework({ x: d.x + (Math.random() - 0.5) * this.sigma, y: d.y }, 200 + i * 170);
    }
  }

  render(): void {
    this.app.renderer.render(this.app.stage);
  }

  /** Reaktioner på simulationens hændelser. */
  events(evs: readonly SimEvent[], state: State): void {
    for (const e of evs) {
      switch (e.t) {
        case 'line_built': {
          // Tegnes ind på 320 ms (frame() kopierer tweenen til drawIn), og så løber den første
          // strøm én gang hen ad ledningen: man ser, at den virker, og hvilken vej strømmen går.
          const o = { t: 0 };
          this.drawing.set(e.line, o);
          this.drawIn.set(e.line, 0);
          this.tweens.to(o, { t: 1 }, 320, ease.outCubic, () => {
            if (this.drawing.get(e.line) !== o) return;
            this.drawing.delete(e.line);
            this.drawIn.delete(e.line);
            if (this.opts.calm) return;
            const s = { u: 0 };
            this.surges.set(e.line, s);
            this.tweens.to(s, { u: 1 }, SURGE_MS, ease.inOutQuad, () => {
              if (this.surges.get(e.line) === s) this.surges.delete(e.line);
            });
          });
          const l = state.lines.find((x) => x.id === e.line);
          if (l) {
            this.cities.squash(l.a);
            this.cities.squash(l.b);
            const m = this.lineMidWorld(l.id);
            if (m) {
              setTimeout(() => this.fx.puff(m, 6, C.white, 0.8), 300);
              if (e.cost > 0) this.fx.text(m, `-${e.cost} kr`, C.white, -34);
            }
          }
          break;
        }
        case 'line_upgraded': {
          const l = state.lines.find((x) => x.id === e.line);
          const m = this.lineMidWorld(e.line);
          if (l) {
            this.cities.squash(l.a);
            this.cities.squash(l.b);
          }
          if (m) {
            this.fx.sparks(m, 8, C.yellow);
            this.fx.ring(m, C.green, 0.6);
            // Over ringen, ikke inde i den.
            if (e.cost > 0) this.fx.text(m, `-${e.cost} kr`, C.white, -46);
          }
          break;
        }
        case 'line_buried': {
          const m = this.lineMidWorld(e.line);
          if (m) this.fx.puff(m, 8, C.sand, 0.9);
          break;
        }
        case 'line_removed': {
          const m = this.lineMidWorld(e.line);
          if (m) {
            this.fx.puff(m, 8, C.white, 1);
            if (e.refund > 0) this.fx.text(m, `+${e.refund} kr`, C.yellow, -14);
          }
          break;
        }
        case 'line_broken':
        case 'line_half': {
          const m = this.lineMidWorld(e.line);
          if (m) {
            this.fx.sparks(m, 10, C.orange);
            this.fx.puff(m, 5, C.stoneDim, 0.8);
          }
          if (e.t === 'line_broken') {
            this.fx.shake(4);
            this.hitStop(60);
          }
          break;
        }
        case 'line_saved': {
          // Reddet: et kort frys, så man når at se, at forstærkningen holdt.
          const m = this.lineMidWorld(e.line);
          if (m) {
            this.fx.ring(m, C.green, 0.8);
            this.fx.sparks(m, 6, C.white);
          }
          const l = state.lines.find((x) => x.id === e.line);
          if (l) {
            this.cities.squash(l.a);
            this.cities.squash(l.b);
          }
          this.hitStop(90);
          break;
        }
        case 'mast_reinforced': {
          const m = this.lineMidWorld(e.line);
          if (m) this.fx.ring(m, C.petrol, 0.7);
          break;
        }
        case 'line_repaired': {
          const m = this.lineMidWorld(e.line);
          if (m) {
            this.fx.ring(m, C.green, 0.8);
            this.fx.sparks(m, 6, C.green);
            if (e.cost) this.fx.text(m, `-${e.cost} kr`, C.white, -34);
          }
          break;
        }
        case 'station_upgraded':
        case 'battery_built':
        case 'flywheel_built':
        case 'city_grew': {
          this.cities.squash(e.node);
          const d = this.disp.get(e.node);
          if (d) this.fx.sparks(d, 8, C.yellow);
          break;
        }
        case 'producer_connected': {
          const d = this.disp.get(e.node);
          if (d) {
            this.fx.ring(d, C.green, 1);
            if (e.fee > 0) this.fx.text(d, `+${e.fee} kr`, C.yellow, -64);
          }
          break;
        }
        case 'blackout': {
          const d = this.disp.get(e.node);
          if (d) {
            this.fx.ring(d, C.red, 1.1);
            this.fx.puff(d, 8, C.cloud, 1);
          }
          this.cities.squash(e.node);
          this.fx.shake(9);
          this.hitStop(140);
          break;
        }
        case 'relit':
          this.cities.squash(e.node);
          break;
        case 'rescue': {
          // Reddet i sidste øjeblik: byen klemmes, en grøn ring og et splitsekunds frys.
          const d = this.disp.get(e.node);
          if (d) {
            this.fx.ring(d, C.green, 1);
            this.fx.sparks(d, 6, C.white);
          }
          this.cities.squash(e.node);
          this.hitStop(80);
          break;
        }
        case 'quick_connect': {
          const d = this.disp.get(e.node);
          if (d) {
            this.fx.ring(d, C.yellow, 0.9);
            this.fx.bolt(d, -40);
          }
          this.cities.squash(e.node);
          break;
        }
        case 'smaa': {
          // Gæsten lander på ledningen med et lille pust.
          const m = this.lineMidWorld(e.line);
          if (m) this.fx.puff(m, 5, C.white, 0.6);
          break;
        }
        case 'level_won':
          this.victory();
          break;
        case 'threat_hit': {
          if (e.kind === 'storm') {
            this.glimt(C.white, 0.28, 260);
            for (const id of e.lines) {
              const m = this.lineMidWorld(id);
              if (m) this.fx.bolt(m, -30);
            }
          } else if (e.kind === 'dunkelflaute') {
            // Vinden lægger sig: et gråt glimt over kortet.
            this.glimt(C.dusk, 0.14, 700);
          } else if (e.kind === 'cable_half') {
            const m = this.lineMidWorld(e.lines[0] ?? '');
            if (m) this.fx.sparks(m, 8, C.orange);
          } else if (e.kind !== 'plant_trip') {
            this.cardHit(e.threat, state);
          }
          break;
        }
        case 'freq_dip': {
          // Frekvensen ved værket: grøn over 49,5 Hz, gul imellem. Under 49,0 kobler nettet en by fra.
          const d = this.disp.get(e.node);
          if (d) {
            this.fx.puff(d, 10, C.smoke, 1.2);
            const hz = `${(e.nadir / 100).toFixed(1).replace('.', ',')} Hz`;
            this.fx.text(d, hz, e.nadir >= CALM_CHZ ? C.green : e.shed ? C.white : C.yellow, -60);
          }
          if (e.shed) {
            const c = this.disp.get(e.shed);
            if (c) this.fx.ring(c, C.red, 1);
            if (!this.opts.calm) this.cities.hop(e.shed, 0, 10);
          }
          break;
        }
        case 'card_answered': {
          // Figuren hopper, og svaret lander med gnister. Et betalt svar viser prisen.
          this.threats.pop(e.threat);
          const sp = this.threats.screenOf(e.threat);
          if (!sp) break;
          const w = this.cam.toWorld(sp);
          if (e.auto) {
            this.fx.puff(w, 5, C.white, 0.7);
          } else {
            this.fx.ring(w, e.answer === 'build' || e.answer === 'ride' || e.answer === 'wait' ? C.orange : C.green, 0.7);
            this.fx.sparks(w, 7, C.yellow);
            if (e.cost > 0) this.fx.text(w, `-${e.cost} kr`, C.white, -40);
          }
          break;
        }
        case 'power_used': {
          if (e.power === 'fcr') {
            // Lynreserve: et gult glimt, og alle byer med strøm hopper i en bølge fra midten.
            this.glimt(C.yellow, 0.2, 340);
            const ids = this.cities.litIds();
            let cx = 0;
            let cy = 0;
            for (const id of ids) {
              const d = this.disp.get(id);
              if (d) {
                cx += d.x;
                cy += d.y;
              }
            }
            const c = { x: cx / Math.max(1, ids.length), y: cy / Math.max(1, ids.length) };
            for (const id of ids) {
              const d = this.disp.get(id);
              if (!d) continue;
              const delay = Math.min(700, Math.hypot(d.x - c.x, d.y - c.y) * 1.2);
              this.fx.ring(d, C.yellow, 0.8, delay);
              if (!this.opts.calm) this.cities.hop(id, delay, 9);
            }
            // Mørke byer får lyn: de får ekstra tid.
            for (const n of state.nodes) {
              if (!n.city || !n.active || isLit(n)) continue;
              const d = this.disp.get(n.id);
              if (d) this.fx.bolt(d, -40);
            }
            this.fx.shake(3);
          } else if (e.power === 'shed') {
            // Nødaflastning: byen lukker øjnene et øjeblik. Petrol, ikke rød: den går ikke i sort.
            const d = this.disp.get(e.target);
            if (d) {
              this.fx.ring(d, C.petrol, 1);
              this.fx.puff(d, 6, C.stoneDim, 0.8);
              this.fx.text(d, 'Pause 10 s', C.white, -64);
            }
            this.cities.squash(e.target);
          } else {
            // Ring til Norge: vandkraften lander i en turkis bølge.
            const d = this.disp.get(e.target);
            if (d) {
              for (let i = 0; i < 3; i++) this.fx.ring(d, C.turquoise, 0.9 + i * 0.35, i * 220);
              this.fx.sparks(d, 10, C.turquoise);
              this.fx.text(d, 'Hej fra Norge!', C.white, -64);
              this.cities.squash(e.target);
            }
            this.hitStop(60);
          }
          this.pulseAt = 0;
          break;
        }
        case 'power_ended': {
          if (e.power === 'shed') {
            // Byen vågner igen.
            const id = state.power.shed.target;
            const d = this.disp.get(id);
            if (d) this.fx.ring(d, C.green, 0.8);
            if (!this.opts.calm) this.cities.hop(id, 0, 10);
          }
          break;
        }
        case 'threat_shooed': {
          const p = this.threats.screenOf(e.threat);
          if (p) {
            const w = this.cam.toWorld(p);
            this.fx.puff(w, 8, C.white, 0.9);
            // Småt-gæsten hopper væk med et "+1 kr": tappet giver en krone.
            if (e.kind === 'smaa' && e.amount) this.fx.text(w, `+${e.amount} kr`, C.yellow, -36);
          }
          break;
        }
        case 'phantom_vanished': {
          const d = this.disp.get(e.node);
          if (d) this.fx.puff(d, 10, C.white, 1.2);
          break;
        }
        case 'crew_hired': {
          const c = state.crews.find((x) => x.id === e.crew);
          if (c) this.fx.puff(this.warp({ x: c.x, y: c.y }), 6, C.white, 0.7);
          break;
        }
        default:
          break;
      }
    }
  }

  /** Et hændelseskort rammer: bølgen ruller ind over byerne eller ledningerne. */
  private cardHit(id: string, state: State): void {
    const t = state.threats.find((x) => x.id === id);
    if (!t) return;
    this.threats.pop(id);
    if (t.kind === 'spike') {
      // Elbilerne sætter stikket i: byerne ryster, og et orange "+30 %" stiger op.
      for (const nid of t.nodes ?? []) {
        const d = this.disp.get(nid);
        if (!d) continue;
        this.fx.ring(d, C.orange, 0.9);
        this.fx.sparks({ x: d.x, y: d.y - 10 }, 5, C.orange);
        this.cities.squash(nid);
      }
      const d0 = this.disp.get(t.nodes?.[0] ?? '');
      const pct = t.card?.answer === 'smart' ? Math.round((t.pct ?? 30) / 2) : (t.pct ?? 30);
      if (d0) this.fx.text(d0, `+${pct} %`, C.orange, -70);
    } else if (t.kind === 'heatwave') {
      // Hedebølgen: et varmt glimt, og de varme ledninger damper.
      this.glimt(C.orange, 0.12, 600);
      for (const lid of t.atRisk) {
        const m = this.lineMidWorld(lid);
        if (m) this.fx.puff(m, 4, C.sparkWarm, 0.8);
      }
    } else if (t.kind === 'dc_rush') {
      // De nye kunder lander med et turkis pust.
      for (const nid of t.nodes ?? []) {
        const n = state.nodes.find((x) => x.id === nid);
        const d = this.disp.get(nid);
        if (!n?.active || !d) continue;
        this.fx.ring(d, C.turquoise, 0.9);
        this.fx.puff(d, 6, C.white, 0.8);
        this.cities.squash(nid);
      }
    }
  }

  private lineMidWorld(id: string): P | null {
    const g = this.lines.get(id);
    if (g) return this.cam.toWorld(bez(g.a, g.c, g.b, 0.5));
    const l = this.state?.lines.find((x) => x.id === id);
    if (!l) return null;
    const a = this.disp.get(l.a);
    const b = this.disp.get(l.b);
    if (!a || !b) return null;
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  // -------------------------------------------------------------------------
  // Hit-test og skærmpositioner

  /** Synlige bynavnes skærmrektangler (UI-testen tjekker, at hånden ikke dækker dem). */
  labelRects(): Array<{ x: number; y: number; w: number; h: number }> {
    return this.cities.labelRects();
  }

  /** Testkrog: noders krop og navn på skærmen, og kameraets stilling. */
  /** Testkrog (scripts/runde2test.mjs): et langsomt glid, som et træk skal kunne fryse. */
  debugGlide(dx: number, dy: number, ms: number): void {
    const v = this.cam.view();
    this.cam.glideTo({ ...v, ox: v.ox + dx, oy: v.oy + dy }, ms);
  }

  mapProbe(): { nodes: ReturnType<CitiesLayer['nodeBoxes']>; objects: MapObject[]; cam: CamView; fitScale: number; stuck: string[]; gliding: boolean; labels: CitiesLayer['lastLabels'] } {
    const objects = [...this.cities.objectBoxes(), ...this.units.objectBoxes(), ...(this.state ? this.threats.objectBoxes(this.state) : [])];
    return { nodes: this.cities.nodeBoxes(), objects, cam: this.cam.view(), fitScale: this.cam.fitScale, stuck: [...this.stuck], gliding: this.gliding, labels: this.cities.lastLabels };
  }

  get k(): number {
    return this.view?.k ?? 1;
  }

  /** Nodens "krop" på skærmen. */
  nodeScreen(id: string): P | null {
    const p = this.cam.toScreen(this.disp.get(id) ?? { x: NaN, y: NaN });
    if (!Number.isFinite(p.x)) return null;
    return { x: p.x, y: p.y - 14 * this.k };
  }

  lineScreen(id: string): P | null {
    const g = this.lines.get(id);
    return g ? bez(g.a, g.c, g.b, 0.5) : null;
  }

  /**
   * Hvor hånden skal pege på en ledning: det punkt på den midterste del, hvor hånden (der hænger
   * ned og til højre fra fingerspidsen) dækker færrest byer, møller og navne.
   */
  handPoint(id: string, inStorm = false): P | null {
    const g = this.lines.get(id);
    if (!g) return null;
    const occ = this.cities.occupied;
    // Forstærkning: fingeren skal ligge inde i stormens spor, ikke på den del af ledningen, der går
    // fri (playtest runde 3: "hånden peger uden for sporet").
    const band = inStorm ? this.stormBand() : null;
    let best: P | null = null;
    let bestScore = Infinity;
    const steps = band ? 28 : 14;
    for (let i = 0; i <= steps; i++) {
      const t = band ? 0.06 + (0.88 * i) / steps : 0.18 + (0.64 * i) / steps;
      const p = bez(g.a, g.c, g.b, t);
      const r = { x0: p.x - 18, y0: p.y - 6, x1: p.x + 36, y1: p.y + 74 };
      let score = Math.abs(t - 0.5) * 20;
      if (band) {
        const mid = (band.y0 + band.y1) / 2;
        const half = Math.max(1, (band.y1 - band.y0) / 2);
        const off = Math.abs(p.y - mid) / half;
        score += off > 0.85 ? 400 + off * 100 : off * 30;
      }
      for (const o of occ) {
        const w = Math.min(r.x1, o.x1) - Math.max(r.x0, o.x0);
        const h = Math.min(r.y1, o.y1) - Math.max(r.y0, o.y0);
        if (w > 0 && h > 0) score += (w * h) / 40;
      }
      if (score < bestScore) {
        bestScore = score;
        best = p;
      }
    }
    return best;
  }

  /** Det varslede stormspor på skærmen (øverste og nederste y), eller null uden storm på vej. */
  stormBand(): { y0: number; y1: number } | null {
    const t = this.state?.threats.find((x) => x.kind === 'storm' && x.phase === 'warning');
    if (!t) return null;
    const a = this.cam.toScreen({ x: 0, y: t.trackY - t.trackHalf });
    const b = this.cam.toScreen({ x: 0, y: t.trackY + t.trackHalf });
    return { y0: Math.min(a.y, b.y), y1: Math.max(a.y, b.y) };
  }

  /** Enhedsnormalen på ledningens midte (skærm), den side der vender opad. */
  lineNormal(id: string): P | null {
    const g = this.lines.get(id);
    if (!g) return null;
    const dx = g.b.x - g.a.x;
    const dy = g.b.y - g.a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1) return null;
    const nx = -dy / len;
    const ny = dx / len;
    return ny <= 0 ? { x: nx, y: ny } : { x: -nx, y: -ny };
  }

  threatScreen(id: string): P | null {
    return this.threats.screenOf(id);
  }

  hitNode(p: P, radius = 0, except: string | null = null): string | null {
    const state = this.state;
    if (!state) return null;
    const r = Math.max(26, 26 * this.k) + radius;
    let best: string | null = null;
    let bestD = r;
    for (const n of state.nodes) {
      if (!n.active || n.id === except) continue;
      const c = this.nodeScreen(n.id);
      if (!c) continue;
      const d = Math.hypot(c.x - p.x, c.y - p.y);
      if (d < bestD) {
        bestD = d;
        best = n.id;
      }
    }
    return best;
  }

  /**
   * Som hitNode, men også mod hvor noderne stod i kameraets billeder de seneste 400 ms. Bruges kun,
   * når fingeren lander (dragSource): fingeren sigtede efter det, den så, ikke det, kameraet nåede.
   */
  hitNodeRecent(p: P, radius = 0): string | null {
    const hit = this.hitNode(p, radius);
    if (hit || !this.state) return hit;
    const r = Math.max(26, 26 * this.k) + radius;
    const lift = 14 * this.k;
    let best: string | null = null;
    let bestD = r;
    for (const { v } of this.recent) {
      for (const n of this.state.nodes) {
        if (!n.active) continue;
        const d0 = this.disp.get(n.id);
        if (!d0) continue;
        const x = d0.x * v.scale + v.ox;
        const y = d0.y * v.scale * v.ky + v.oy - lift;
        const d = Math.hypot(x - p.x, y - p.y);
        if (d < bestD) {
          bestD = d;
          best = n.id;
        }
      }
    }
    return best;
  }

  hitLine(p: P): string | null {
    const state = this.state;
    if (!state) return null;
    let best: string | null = null;
    let bestD = Infinity;
    for (const l of state.lines) {
      const g = this.lines.get(l.id);
      if (!g) continue;
      const d = bezDist(p, g.a, g.c, g.b);
      // Mindst 52 px trykflade i alt (26 px til hver side), også når kortet er zoomet ud.
      const tol = Math.max(26, lineWidth(l, this.k) / 2 + 16);
      if (d < tol && d < bestD) {
        bestD = d;
        best = l.id;
      }
    }
    return best;
  }

  /** Afstand på skærmen fra punktet til nodens krop. */
  nodeDist(id: string, p: P): number {
    const c = this.nodeScreen(id);
    return c ? Math.hypot(c.x - p.x, c.y - p.y) : Infinity;
  }

  /** Afstand på skærmen fra punktet til ledningen. */
  lineDist(id: string, p: P): number {
    const g = this.lines.get(id);
    return g ? bezDist(p, g.a, g.c, g.b) : Infinity;
  }

  hitThreat(p: P): string | null {
    return this.view ? this.threats.hit(p, this.view) : null;
  }

  /** Ikonet for en trussel, der ikke kan jages, under punktet (se threats-layer near). */
  nearThreat(p: P): string | null {
    return this.view ? this.threats.near(p, this.view) : null;
  }

  // -------------------------------------------------------------------------
  // Forhåndsvisning og lup

  preview(from: string | null, to: P | null, ok: boolean, snapped: boolean): void {
    const a = from ? this.nodeScreen(from) : null;
    this.grid.drawPreview(a, to, ok, snapped, this.k, performance.now());
  }

  /** Ledninger, der bygges, når uret går igen (Orkan i pause). Tom liste rydder dem. */
  private hadPlanned = false;
  planned(pairs: readonly [string, string][]): void {
    if (!pairs.length && !this.hadPlanned) return;
    this.hadPlanned = pairs.length > 0;
    const pts: [P, P][] = [];
    for (const [a, b] of pairs) {
      const pa = this.nodeScreen(a);
      const pb = this.nodeScreen(b);
      if (pa && pb) pts.push([pa, pb]);
    }
    this.grid.drawPlanned(pts, this.k);
  }

  /** Luppen over fingeren på telefonen. `at` er fingerens punkt, eller null for at skjule den. */
  loupe(at: P | null): void {
    this.loupeAt = at;
    this.loupeC.visible = at !== null;
  }

  private renderLoupe(): void {
    const at = this.loupeAt;
    if (!at) return;
    const R = LOUPE_R;
    if (!this.loupeRT) {
      this.loupeRT = RenderTexture.create({ width: R * 2, height: R * 2, resolution: Math.min(2, this.quality.settings.resolution) });
      this.loupeSprite = new Sprite(this.loupeRT);
      this.loupeSprite.anchor.set(0.5);
      const mask = new Graphics().circle(0, 0, R).fill(C.white);
      this.loupeSprite.mask = mask;
      this.loupeC.addChildAt(mask, 0);
      this.loupeC.addChildAt(this.loupeSprite, 0);
    }
    const m = new Matrix().translate(-at.x, -at.y).scale(LOUPE_ZOOM, LOUPE_ZOOM).translate(R, R);
    this.app.renderer.render({ container: this.root, target: this.loupeRT, clear: true, clearColor: [0.81, 0.925, 0.92, 1], transform: m });
    const w = this.app.screen.width;
    let cx = at.x;
    let cy = at.y - R - 54;
    if (cy < R + 8) cy = at.y + R + 54;
    cx = Math.max(R + 8, Math.min(w - R - 8, cx));
    this.loupeC.position.set(cx, cy);
    const g = this.loupeRing;
    g.clear();
    g.circle(0, 4, R + 3).fill({ color: C.ink, alpha: 0.12 });
    g.circle(0, 0, R).stroke({ width: 5, color: C.white });
    g.circle(0, 0, R + 2.5).stroke({ width: 1.5, color: C.ink, alpha: 0.4 });
    g.moveTo(-6, 0).lineTo(6, 0).moveTo(0, -6).lineTo(0, 6).stroke({ width: 2, color: C.ink, alpha: 0.6, cap: 'round' });
    this.loupeC.setChildIndex(g, this.loupeC.children.length - 1);
  }

  /** Efter tabt WebGL-kontekst: mal teksturerne igen og tegn kortet. */
  restored(): void {
    this.atlas.rebake();
    this.loupeRT = null;
    this.loupeC.removeChildren();
    this.loupeC.addChild(this.loupeRing);
    this.buildMap();
  }
}
